import React, { useEffect, useRef, useState } from 'react'

import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps'
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material'

// Parsed result handed back when the user picks a Places suggestion — address
// fields + geocoding (latitude/longitude feed the analytics scan map).
export type ParsedAddress = {
  address_line: string
  city: string
  state: string | null
  country: string // ISO 3166-1 alpha-2
  postal_code: string
  latitude: number | null
  longitude: number | null
  place_id: string | null
  formatted_address: string | null
}

export interface AddressFieldProps {
  // Google Maps API key (passed by the app, like @repo/ui Map). Without it the
  // field degrades to a plain text input so manual entry still works.
  apiKey?: string
  placeholder?: string
  defaultValue?: string
  onSelect: (parsed: ParsedAddress) => void
  // Fires as the user types free text, so manual entry still drives the line.
  onInputChange?: (value: string) => void
  error?: boolean
  disabled?: boolean
  /**
   * ISO 3166-1 alpha-2 code that SCOPES the suggestions to one country.
   *
   * Optional, and omitting it keeps the previous worldwide behaviour, so every
   * existing caller is unaffected. Passing it matters wherever the form already
   * asks for a country separately (the signup billing address does): without
   * scoping, "1600 Radio Way" offers matches from a dozen countries and the
   * chosen suggestion can silently overwrite the country the visitor just
   * picked, which reads as the form fighting them.
   */
  countryCode?: string | null
}

type Suggestion = { id: string; label: string; prediction: any }

// Inline debounce so the field has no app-level hook dependency.
function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(handle)
  }, [value, delay])
  return debounced
}

// Filled input styled to match @repo/ui Input: border + radius, brand focus,
// disableUnderline (no underline / notched outline = no stray line under the
// field). Height pinned to 49px so it lines up with every sibling form field
// (the filled variant otherwise reserves label space and renders ~68px tall).
const inputSx = (theme: any) => ({
  '& .MuiFilledInput-root': {
    height: '49px',
    paddingTop: 0,
    paddingBottom: 0,
    backgroundColor: theme.palette.primary?.[50] || '#f6f6f6',
    // Match the design-system field (@repo/ui Input): primary[100] rest border
    // (the old grey[200] read as an odd, more-visible line vs sibling fields) and
    // brand[600] border + soft ring on hover/focus.
    border: `1px solid ${theme.palette.primary?.[100] || '#e7e7e7'}`,
    borderRadius: '8px',
    // Match the @repo/ui Input's 16px so placeholders/values are the same size
    // across every field in the address form.
    fontSize: '16px',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    '&:hover': {
      backgroundColor: theme.palette.primary?.[50] || '#f6f6f6',
      borderColor: theme.palette.brand?.[600] || '#7F48DF',
    },
    '&.Mui-focused, &:focus-within': {
      backgroundColor: theme.palette.primary?.[50] || '#f6f6f6',
      borderColor: theme.palette.brand?.[600] || '#7F48DF',
      boxShadow: `0 0 0 3px ${theme.palette.brand?.['600-6%'] || 'rgba(127,72,223,0.16)'}`,
    },
    '&:before, &:after': { display: 'none' },
  },
  // Center the text in the fixed 49px height (the app's MuiAutocomplete theme
  // already zeroes paddingTop; zero the bottom too).
  '& .MuiFilledInput-input': {
    paddingTop: 0,
    paddingBottom: 0,
  },
})

const componentText = (components: any[], type: string, useShort = false): string => {
  const c = (components || []).find((comp: any) => (comp.types || []).includes(type))
  if (!c) return ''
  return (useShort ? c.shortText : c.longText) ?? c.short_name ?? c.long_name ?? ''
}

const AddressFieldInner = ({
  placeholder,
  defaultValue,
  onSelect,
  onInputChange,
  error,
  disabled,
  countryCode,
}: AddressFieldProps) => {
  const places = useMapsLibrary('places') as any
  const [inputValue, setInputValue] = useState(defaultValue ?? '')
  const debounced = useDebounced(inputValue, 300)
  const [options, setOptions] = useState<Suggestion[]>([])
  const [loading, setLoading] = useState(false)
  const sessionTokenRef = useRef<any>(null)

  const ensureToken = () => {
    if (!sessionTokenRef.current && places?.AutocompleteSessionToken) {
      sessionTokenRef.current = new places.AutocompleteSessionToken()
    }
    return sessionTokenRef.current
  }

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      if (!places?.AutocompleteSuggestion || !debounced || debounced.trim().length < 3) {
        setOptions([])
        return
      }
      try {
        setLoading(true)
        const { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: debounced,
          sessionToken: ensureToken(),
          // Scope to the picked country when the caller supplies one. Spread
          // rather than set unconditionally: the Places API rejects an empty
          // `includedRegionCodes` array, so "no country chosen yet" has to mean
          // "don't send the key at all", not "send an empty filter".
          ...(countryCode ? { includedRegionCodes: [countryCode.toLowerCase()] } : {}),
        })
        if (cancelled) return
        setOptions(
          (suggestions || [])
            .filter((s: any) => s.placePrediction)
            .map((s: any) => ({
              id: s.placePrediction.placeId,
              label: s.placePrediction.text?.text ?? '',
              prediction: s.placePrediction,
            })),
        )
      } catch {
        if (!cancelled) setOptions([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
    // `countryCode` is a dependency: changing the country must re-run the query
    // against the new region, not leave the previous country's suggestions up.
  }, [debounced, places, countryCode])

  const handleSelect = async (sugg: Suggestion) => {
    try {
      const place = sugg.prediction.toPlace()
      await place.fetchFields({ fields: ['addressComponents', 'location', 'formattedAddress', 'id'] })
      const comps = place.addressComponents ?? []
      const address_line =
        [componentText(comps, 'street_number'), componentText(comps, 'route')].filter(Boolean).join(' ') ||
        (place.formattedAddress?.split(',')[0] ?? sugg.label)
      const parsed: ParsedAddress = {
        address_line,
        city:
          componentText(comps, 'locality') ||
          componentText(comps, 'postal_town') ||
          componentText(comps, 'sublocality_level_1'),
        state: componentText(comps, 'administrative_area_level_1', true) || null,
        country: componentText(comps, 'country', true),
        postal_code: componentText(comps, 'postal_code'),
        latitude: place.location?.lat?.() ?? null,
        longitude: place.location?.lng?.() ?? null,
        place_id: place.id ?? sugg.id ?? null,
        formatted_address: place.formattedAddress ?? null,
      }
      sessionTokenRef.current = null
      setInputValue(parsed.address_line)
      onSelect(parsed)
    } catch {
      /* ignore — user can still type manually */
    }
  }

  return (
    <Autocomplete
      freeSolo
      disabled={disabled}
      options={options}
      loading={loading}
      filterOptions={x => x}
      getOptionLabel={o => (typeof o === 'string' ? o : o.label)}
      inputValue={inputValue}
      onInputChange={(_, v, reason) => {
        if (reason === 'input') {
          setInputValue(v)
          onInputChange?.(v)
        }
      }}
      onChange={(_, value) => {
        if (value && typeof value !== 'string') handleSelect(value)
      }}
      noOptionsText={
        <Typography variant="body3" color="text.secondary">
          {debounced.trim().length < 3 ? 'Keep typing…' : 'No matches'}
        </Typography>
      }
      loadingText={
        <Typography variant="body3" color="text.secondary">
          Loading…
        </Typography>
      }
      slotProps={{
        paper: {
          sx: (theme: any) => ({
            // The house trigger→menu gap, absolute px because this is a layer-1
            // component (§47) — `mt: 1` rendered 4px under the platform theme and 8px
            // under the app's. This is an Autocomplete popper, so it cannot spread
            // MENU_PAPER_SX; the value is stated instead.
            mt: '8px',
            borderRadius: 2,
            border: `1px solid ${theme.palette.primary?.[100] || '#e6e6e6'}`,
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            backgroundColor: theme.palette.background.paper,
            // Kill MUI's dark-mode elevation overlay (the grey lightening) so this
            // dropdown matches the app's Select/Menu dropdowns — MuiMenu does the
            // same globally, but the Autocomplete popper is a plain Paper that
            // otherwise keeps the overlay and reads lighter/off.
            backgroundImage: 'none',
            // Inset, rounded options with the app's tinted hover. Explicit
            // high-specificity selectors (option class + state) beat MUI's
            // built-in `.MuiAutocomplete-option.Mui-focused` action.hover rule.
            '& .MuiAutocomplete-option': {
              margin: '4px 8px',
              borderRadius: '8px',
            },
            '& .MuiAutocomplete-option:hover, & .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option[aria-selected="true"]':
              {
                backgroundColor: theme.palette.primary?.[50] || '#f5f5f5',
              },
            '& .MuiAutocomplete-loading, & .MuiAutocomplete-noOptions': {
              padding: '8px 12px',
            },
          }),
        },
      }}
      renderOption={(props, option) => (
        // Inline minHeight/padding beats MUI's default option styles (the
        // `& .MuiAutocomplete-option` sx loses to them), matching the app's
        // standard dropdown item height (ActionDropdown: 44px + 12px padding).
        <li
          {...props}
          key={option.id}
          style={{ ...((props as any).style || {}), minHeight: 44, padding: '12px' }}
        >
          <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0, width: '100%' }}>
            <Typography variant="body3" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {option.label}
            </Typography>
          </Box>
        </li>
      )}
      renderInput={params => (
        <TextField
          {...params}
          variant="filled"
          placeholder={placeholder ?? 'Start typing an address…'}
          error={error}
          sx={inputSx}
          InputProps={{
            ...params.InputProps,
            disableUnderline: true,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )
}

/**
 * Address autocomplete field backed by Google Places (the new
 * AutocompleteSuggestion API). On select it returns the parsed address
 * components + geocoding. Self-contained: wraps itself in APIProvider with the
 * supplied apiKey. Degrades to a plain text input when no key is configured so
 * manual entry always works. Visually identical to @repo/ui Input.
 */
export const AddressField = ({ apiKey, ...props }: AddressFieldProps) => {
  if (!apiKey) {
    return (
      <TextField
        fullWidth
        variant="filled"
        placeholder={props.placeholder ?? 'Enter address'}
        defaultValue={props.defaultValue}
        error={props.error}
        disabled={props.disabled}
        sx={inputSx}
        InputProps={{ disableUnderline: true }}
        onChange={e => props.onInputChange?.(e.target.value)}
      />
    )
  }
  return (
    <APIProvider apiKey={apiKey}>
      <AddressFieldInner {...props} />
    </APIProvider>
  )
}
